// Multi-platform property utilities for handling different property sources

/**
 * Unwrap a search URL if it was erroneously saved as a proxy URL.
 * When adding a search from localhost, users may paste the proxy URL
 * (e.g. http://localhost:8787/?url=https://www.buysellcyprus.com/...)
 * instead of the real target URL. Returns the real URL for display, copy, and scanning.
 * @param {string} url - Stored search URL (may be proxy or real)
 * @returns {string} - The real target URL
 */
export function unwrapProxySearchUrl(url) {
  if (!url || typeof url !== 'string') return url || '';
  try {
    const parsed = new URL(url);
    const urlParam = parsed.searchParams.get('url');
    if (!urlParam) return url;
    // Only unwrap if this looks like our property proxy (localhost:8787 or property-proxy workers)
    const isProxy =
      (parsed.hostname === 'localhost' && parsed.port === '8787') ||
      /property-proxy[.\w-]*\.workers\.dev$/i.test(parsed.hostname);
    if (isProxy) return decodeURIComponent(urlParam);
  } catch {
    // Invalid URL, return as-is
  }
  return url;
}

/**
 * Supported property platforms
 */
export const PLATFORMS = {
  BAZARAKI: 'bazaraki',
  FACEBOOK: 'facebook',
  BUYSELL: 'buysell',
  DOMCOM: 'domcom',
  ALTIA: 'altia',
  REMU: 'remu',
};

/**
 * Platform configurations with URL patterns and ID extraction rules
 */
const PLATFORM_CONFIGS = {
  [PLATFORMS.BAZARAKI]: {
    name: 'Bazaraki',
    urlPatterns: [
      /^https?:\/\/(www\.)?bazaraki\.com\//i
    ],
    searchUrlPatterns: [
      /^https?:\/\/(www\.)?bazaraki\.com\/api\/items\/adverts-geometry\//i,
      /^https?:\/\/(www\.)?bazaraki\.com\/ajax-items-list\//i,
      /^https?:\/\/(www\.)?bazaraki\.com\/real-estate-(for-sale|to-rent)\//i,
    ],
    idExtraction: {
      pattern: /\/adv\/(\d+)(?:_|\/|\?|$)/,
      groupIndex: 1
    },
    displayFormat: 'Bazaraki #{id}'
  },
  [PLATFORMS.FACEBOOK]: {
    name: 'Facebook Marketplace',
    urlPatterns: [
      /^https?:\/\/(www\.)?facebook\.com\/marketplace\/item\/(\d+)/i,
      /^https?:\/\/(www\.)?facebook\.com\/.*\/posts\/(\d+)/i
    ],
    searchUrlPatterns: [],
    idExtraction: {
      pattern: /\/(?:item|posts)\/(\d+)/,
      groupIndex: 1
    },
    displayFormat: 'Facebook #{id}'
  },
  [PLATFORMS.BUYSELL]: {
    name: 'BuySell Cyprus',
    urlPatterns: [
      /^https?:\/\/(www\.)?buysellcyprus\.com\/property\//i,
      /^https?:\/\/(www\.)?buysellcyprus\.com\/property-for-sale\//i,
      /^https?:\/\/(www\.)?buysellcyprus\.com\/property-for-rent\//i,
      /^https?:\/\/(www\.)?buysellcyprus\.com\/property-to-rent\//i,
    ],
    searchUrlPatterns: [
      /^https?:\/\/(www\.)?buysellcyprus\.com\/properties-for-sale\//i,
      /^https?:\/\/(www\.)?buysellcyprus\.com\/properties-for-rent\//i,
      /^https?:\/\/(www\.)?buysellcyprus\.com\/new-homes-for-sale\//i,
    ],
    idExtraction: {
      // Matches /property/{id} or slug ending with -{id}
      pattern: /(\d{5,})(?:\/(?:gallery|request-information)?)?(?:\?.*)?$/,
      groupIndex: 1
    },
    displayFormat: 'BuySell #{id}'
  },
  [PLATFORMS.DOMCOM]: {
    name: 'DOM.com.cy',
    urlPatterns: [
      /^https?:\/\/dom\.com\.cy\/(?:en\/|ru\/)?catalog\/(sale|rent)\/\d+/i,
    ],
    searchUrlPatterns: [
      /^https?:\/\/dom\.com\.cy\/(?:en\/|ru\/)?catalog\/(sale|rent)\/[a-z]/i,
    ],
    idExtraction: {
      pattern: /\/catalog\/(?:sale|rent)\/(\d+)/,
      groupIndex: 1
    },
    displayFormat: 'DOM #{id}'
  },
  [PLATFORMS.ALTIA]: {
    name: 'Altia Marketplace',
    urlPatterns: [
      // Detail page: /listings/{id} (optional locale prefix en|el|ru)
      /^https?:\/\/marketplace\.altia\.com\.cy\/(?:[a-z]{2}\/)?listings\/\d+/i,
    ],
    searchUrlPatterns: [
      // Search page: /{category}/{commercialization} e.g. /residential/sale
      /^https?:\/\/marketplace\.altia\.com\.cy\/(?:[a-z]{2}\/)?(residential|commercial|land)\/(sale|rental|rent)/i,
    ],
    idExtraction: {
      pattern: /\/listings\/(\d+)/,
      groupIndex: 1
    },
    displayFormat: 'Altia #{id}'
  },
  [PLATFORMS.REMU]: {
    name: 'REMU (Bank of Cyprus)',
    urlPatterns: [
      // Detail page: /Cyprus/listing-{id}
      /^https?:\/\/(www\.)?remuproperties\.com\/Cyprus\/listing-\d+/i,
    ],
    searchUrlPatterns: [
      // Results page: /Cyprus/property-listing-{Town|District}-{Type}?price=...&area=...
      /^https?:\/\/(www\.)?remuproperties\.com\/Cyprus\/property-listing-/i,
    ],
    idExtraction: {
      pattern: /\/listing-(\d+)/,
      groupIndex: 1
    },
    displayFormat: 'REMU #{id}'
  }
};

/**
 * Extract property ID and platform from a URL
 * @param {string} url - Property URL
 * @returns {Object} - { propertyId, platform, isValid }
 */
export const extractPropertyInfo = (url) => {
  if (!url || typeof url !== 'string') {
    return { propertyId: null, platform: null, isValid: false };
  }

  const cleanUrl = url.trim();
  
  // Check each platform configuration
  for (const [platformKey, config] of Object.entries(PLATFORM_CONFIGS)) {
    // Check if URL matches any of the platform's patterns
    const matchesPattern = config.urlPatterns.some(pattern => pattern.test(cleanUrl));
    
    if (matchesPattern) {
      // Extract property ID using the platform's extraction pattern
      const match = cleanUrl.match(config.idExtraction.pattern);
      if (match && match[config.idExtraction.groupIndex]) {
        return {
          propertyId: match[config.idExtraction.groupIndex],
          platform: platformKey,
          isValid: true
        };
      }
    }
  }

  return { propertyId: null, platform: null, isValid: false };
};

/**
 * Validate if a URL is from a supported platform
 * @param {string} url - Property URL
 * @returns {boolean}
 */
export const isValidPropertyUrl = (url) => {
  const { isValid } = extractPropertyInfo(url);
  return isValid;
};

/**
 * Get platform display name
 * @param {string} platform - Platform key
 * @returns {string}
 */
export const getPlatformDisplayName = (platform) => {
  const config = PLATFORM_CONFIGS[platform];
  return config ? config.name : 'Unknown Platform';
};

/**
 * Format property reference for display (e.g., "Bazaraki #123456")
 * @param {Object} property - Property object with platform and propertyId
 * @returns {string}
 */
export const formatPropertyReference = (property) => {
  if (!property.platform || !property.propertyId) {
    return 'Unknown Property';
  }

  const config = PLATFORM_CONFIGS[property.platform];
  if (!config) {
    return `${property.propertyId}`;
  }

  return config.displayFormat.replace('{id}', property.propertyId);
};

/**
 * Create user-friendly error message for property not found
 * @param {Object} property - Property object
 * @returns {string}
 */
export const formatPropertyNotFoundError = (property) => {
  const reference = formatPropertyReference(property);
  const title = property.title ? `${property.title} (${reference})` : reference;
  return `Property expired: ${title}`;
};

/**
 * Get all supported platforms for UI display
 * @returns {Array} - Array of platform objects with key and name
 */
export const getSupportedPlatforms = () => {
  return Object.entries(PLATFORM_CONFIGS).map(([key, config]) => ({
    key,
    name: config.name
  }));
};

/**
 * Check if property is from Bazaraki (for backward compatibility)
 * @param {string} url - Property URL
 * @returns {boolean}
 */
export const isBazarakiProperty = (url) => {
  const { platform } = extractPropertyInfo(url);
  return platform === PLATFORMS.BAZARAKI;
};

/**
 * Legacy function for backward compatibility
 * @param {string} url - Property URL
 * @returns {boolean}
 */
export const validateBazarakiUrl = (url) => {
  return isBazarakiProperty(url);
};

/**
 * Detect which platform a search URL belongs to
 * @param {string} url - Search URL
 * @returns {string|null} - Platform key or null if not recognized
 */
export const detectSearchPlatform = (url) => {
  if (!url || typeof url !== 'string') return null;

  const cleanUrl = url.trim();

  for (const [platformKey, config] of Object.entries(PLATFORM_CONFIGS)) {
    if (!config.searchUrlPatterns || config.searchUrlPatterns.length === 0) continue;
    const matches = config.searchUrlPatterns.some(pattern => pattern.test(cleanUrl));
    if (matches) return platformKey;
  }

  return null;
};

/**
 * Validate if a URL is a supported search URL from any platform
 * @param {string} url - Search URL
 * @returns {boolean}
 */
export const isValidSearchUrl = (url) => {
  return detectSearchPlatform(url) !== null;
};